import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { CloseOverviewEstimatedTimeByMember } from "@common/schemas/clientsCloseOverview.schema";
import {
    DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
    EstimatedTimeCellVariant,
    EstimatedTimeCellVariantSchema,
} from "@common/types/views/clientsWorkOverviewView";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { resolveColumnVariant } from "@design-system/ViewTable/core/resolveColumnVariant";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { HStack } from "@design-system/HStack/HStack";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { EMPTY_CELL_VALUE } from "./emptyCellValue";
import styles from "./WorkEstimatedTimeCell.module.scss";

const formatMinutes = (minutes: number) => formatDuration(minutes) || "0m";

const memberLabel = (member: CloseOverviewEstimatedTimeByMember) => {
    if (member.userId == null) {
        return "Unassigned";
    }
    return member.preferredName ?? member.email ?? "Unknown";
};

const SUMMARY_PARTS_BY_VARIANT: Record<
    EstimatedTimeCellVariant,
    (times: { remaining: number; done: number }) => {
        primary: number;
        secondary: number;
    }
> = {
    remainingOfTotal: ({ remaining, done }) => ({
        primary: remaining,
        secondary: remaining + done,
    }),
    completedOfTotal: ({ remaining, done }) => ({
        primary: done,
        secondary: remaining + done,
    }),
    remainingOfCompleted: ({ remaining, done }) => ({
        primary: remaining,
        secondary: done,
    }),
};

const Summary = ({
    remaining,
    done,
    variant,
}: {
    remaining: number;
    done: number;
    variant: EstimatedTimeCellVariant;
}) => {
    const { primary, secondary } = SUMMARY_PARTS_BY_VARIANT[variant]({
        remaining,
        done,
    });

    return (
        <span className={styles.summary}>
            <span className={styles.primary}>{formatMinutes(primary)}</span>
            <span className={styles.separator}> / </span>
            <span className={styles.secondary}>{formatMinutes(secondary)}</span>
        </span>
    );
};

// The breakdown always shows raw remaining / done, independent of the cell's
// variant: the `Remaining` and `Done` header rows are the legend for the
// per-member rows below them.
const BreakdownTooltip = ({
    remaining,
    done,
    byMember,
}: {
    remaining: number;
    done: number;
    byMember: CloseOverviewEstimatedTimeByMember[];
}) => (
    <VStack gap={8} className={styles.breakdown}>
        <HStack gap={12} justify="between">
            <span>Remaining</span>
            <span>{formatMinutes(remaining)}</span>
        </HStack>
        <HStack gap={12} justify="between">
            <span>Done</span>
            <span>{formatMinutes(done)}</span>
        </HStack>
        {byMember.length > 0 ? (
            <VStack gap={4} className={styles.memberSection}>
                {byMember.map((member) => (
                    <HStack
                        key={member.userId ?? "unassigned"}
                        gap={12}
                        justify="between"
                        align="center"
                    >
                        <span className={styles.memberLabel}>
                            {memberLabel(member)}
                        </span>
                        <span className={styles.memberTimes}>
                            <span className={styles.primary}>
                                {formatMinutes(member.remainingMinutes)}
                            </span>
                            <span className={styles.separator}> / </span>
                            <span className={styles.secondary}>
                                {formatMinutes(member.doneMinutes)}
                            </span>
                        </span>
                    </HStack>
                ))}
            </VStack>
        ) : null}
    </VStack>
);

/** An application's estimated time in the column's variant, with the per-member breakdown on hover. */
export const WorkEstimatedTime = ({
    row: { estimatedTimeRemaining, estimatedTimeDone, estimatedTimeByMember },
    variant,
    title,
}: {
    row: Pick<
        ClientsWorkOverviewWorkRow,
        "estimatedTimeRemaining" | "estimatedTimeDone" | "estimatedTimeByMember"
    >;
    variant: EstimatedTimeCellVariant;
    /** Heads the breakdown, e.g. the field's name where nothing else says it. */
    title?: string;
}) => (
    <Tooltip
        title={title}
        content={
            <BreakdownTooltip
                remaining={estimatedTimeRemaining}
                done={estimatedTimeDone}
                byMember={estimatedTimeByMember}
            />
        }
        side="bottom"
    >
        <span>
            <Summary
                remaining={estimatedTimeRemaining}
                done={estimatedTimeDone}
                variant={variant}
            />
        </span>
    </Tooltip>
);

export const WorkEstimatedTimeCell = memo(
    ({
        row,
        column,
    }: CellContext<ViewTableFeatures, ClientsWorkOverviewRow, unknown>) => {
        if (row.original.rowType !== "work") {
            return EMPTY_CELL_VALUE;
        }

        const { estimatedTimeRemaining, estimatedTimeDone } = row.original;

        if (estimatedTimeRemaining === 0 && estimatedTimeDone === 0) {
            return (
                <ViewTableCell className={styles.cell}>
                    <span className={styles.empty}>-</span>
                </ViewTableCell>
            );
        }

        const variant = resolveColumnVariant({
            meta: column.columnDef.meta,
            schema: EstimatedTimeCellVariantSchema,
            fallback: DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
        });

        return (
            <ViewTableCell className={styles.cell}>
                <WorkEstimatedTime row={row.original} variant={variant} />
            </ViewTableCell>
        );
    },
);

WorkEstimatedTimeCell.displayName = "WorkEstimatedTimeCell";
